import type * as React from "react";
import { cn } from "@/lib/utils";

// ─── Root ────────────────────────────────────────────────────────────────────

export type ChatTemplateProps = React.ComponentProps<"div">;

export function ChatTemplate({ className, ...props }: ChatTemplateProps) {
  return (
    <div
      className={cn(
        "relative flex h-dvh min-w-0 flex-col bg-background",
        className
      )}
      data-slot="chat-template"
      {...props}
    />
  );
}

// ─── Header ──────────────────────────────────────────────────────────────────

export type ChatTemplateHeaderProps = React.ComponentProps<"header">;

export function ChatTemplateHeader({
  className,
  ...props
}: ChatTemplateHeaderProps) {
  return (
    <header
      className={cn(
        "sticky top-0 z-10 flex items-center gap-2 bg-background px-2 py-1.5",
        className
      )}
      data-slot="chat-template-header"
      {...props}
    />
  );
}

// ─── Header Group ────────────────────────────────────────────────────────────

export type ChatTemplateHeaderGroupProps = React.ComponentProps<"div">;

export function ChatTemplateHeaderGroup({
  className,
  ...props
}: ChatTemplateHeaderGroupProps) {
  return (
    <div
      className={cn("flex items-center gap-2", className)}
      data-slot="chat-template-header-group"
      {...props}
    />
  );
}

// ─── Body ────────────────────────────────────────────────────────────────────

export type ChatTemplateBodyProps = React.ComponentProps<"div">;

export function ChatTemplateBody({
  className,
  ...props
}: ChatTemplateBodyProps) {
  return (
    <div
      className={cn(
        "flex min-w-0 flex-1 flex-col gap-6 overflow-y-auto pt-4",
        className
      )}
      data-slot="chat-template-body"
      {...props}
    />
  );
}

// ─── Footer ──────────────────────────────────────────────────────────────────

export type ChatTemplateFooterProps = React.ComponentProps<"footer">;

export function ChatTemplateFooter({
  className,
  ...props
}: ChatTemplateFooterProps) {
  return (
    <footer
      className={cn(
        "mx-auto flex w-full flex-col gap-1.5 px-4 pt-2 pb-4 md:max-w-3xl md:pb-6",
        className
      )}
      data-slot="chat-template-footer"
      {...props}
    />
  );
}

// ─── Overview ────────────────────────────────────────────────────────────────

export type ChatTemplateOverviewProps = React.ComponentProps<"div">;

export function ChatTemplateOverview({
  className,
  ...props
}: ChatTemplateOverviewProps) {
  return (
    <div
      className={cn(
        "mx-auto mt-[clamp(0.75rem,5vh,4rem)] flex max-w-xl flex-col rounded-xl px-6 py-[clamp(1rem,2.5vh,1.5rem)] text-center leading-relaxed",
        className
      )}
      data-slot="chat-template-overview"
      {...props}
    />
  );
}

// ─── Overview Title ──────────────────────────────────────────────────────────

export type ChatTemplateOverviewTitleProps = React.ComponentProps<"h1">;

export function ChatTemplateOverviewTitle({
  className,
  ...props
}: ChatTemplateOverviewTitleProps) {
  return (
    <h1
      className={cn(
        "mb-[clamp(0.5rem,1.5vh,1rem)] text-balance bg-linear-to-r from-foreground to-foreground/70 bg-clip-text font-bold font-serif text-[clamp(1.8rem,3.7vh,2.55rem)] text-transparent leading-tight tracking-wide",
        className
      )}
      data-slot="chat-template-overview-title"
      {...props}
    />
  );
}

// ─── Overview Subtitle ───────────────────────────────────────────────────────

export type ChatTemplateOverviewSubtitleProps = React.ComponentProps<"h2">;

export function ChatTemplateOverviewSubtitle({
  className,
  ...props
}: ChatTemplateOverviewSubtitleProps) {
  return (
    <h2
      className={cn(
        "mb-[clamp(1rem,3.2vh,2.25rem)] text-balance bg-linear-to-r from-foreground/90 to-foreground/70 bg-clip-text font-medium text-[clamp(1.3rem,3.3vh,2.15rem)] text-transparent leading-tight",
        className
      )}
      data-slot="chat-template-overview-subtitle"
      {...props}
    />
  );
}

// ─── Suggestions ─────────────────────────────────────────────────────────────

export type ChatTemplateSuggestionsProps = React.ComponentProps<"div">;

export function ChatTemplateSuggestions({
  className,
  ...props
}: ChatTemplateSuggestionsProps) {
  return (
    <div
      className={cn(
        "mb-2 grid w-full grid-cols-12 gap-3 pt-1 lg:gap-4",
        className
      )}
      data-slot="chat-template-suggestions"
      {...props}
    />
  );
}
